import React from 'react';

import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {UploadFlowType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ImportedStructuredTableRow} from 'notebook/structured-tables/spreadsheet-import/prepareImportedRows';

import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import {COLUMN_TYPE, IMPORT_TYPE, Pane, PaneKey} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';

function getPanes(
  spreadsheetColumnTypes: Array<COLUMN_TYPE>,
  writableSchemaFieldIds: Array<string>,
  resultSchemaId: string | undefined
): Array<Pane> {
  return [
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({}),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        shouldResolveLinkColumns: false,
        getValidColumnTypes: () => spreadsheetColumnTypes,
        getRequiredColumnsError: () => null,
        getValidSchemaFieldIds: () => writableSchemaFieldIds,
        onValidateSpreadsheet: () => {
          SpreadsheetBulkImportActions.validateRowsForStructuredTableSpreadsheetImport();
        },
        ...(resultSchemaId
          ? {
              savedMappingsConfig: {
                enableSavedMappings: true,
                uploadFlow: UploadFlowType.ADD_ROWS_TO_RESULTS_TABLE,
              },
            }
          : {}),
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: true,
        enableSkipErrors: false,
        ...(resultSchemaId ? {uploadFlow: UploadFlowType.ADD_ROWS_TO_RESULTS_TABLE} : {}),
      }),
    },
  ];
}

export interface BulkAddRowsToStructuredTableFlowProps {
  spreadsheetColumnTypes: Array<COLUMN_TYPE>;
  writableSchemaFieldIds: Array<string>;
  containerSchemaId?: string;
  tagSchemaId?: string;
  schemaInterfaceId?: string;
  resultSchemaId?: string;
  plateSchemaId?: string;
  boxSchemaId?: string;
  processTaskSchemaId?: string;
  processOutputSchemaId?: string;
  onRequestSubmit(parsedRows: Array<ImportedStructuredTableRow>): void;
  // Ignored. Instead, we reset the store state on close using SpreadsheetBulkImportActions.closeModal
  onRequestClose(): void;
  // The maximum number of rows can be imported to a table without exceeding the table row limit. When this value gets passed in, we validate the row count on spreadsheet upload.
  maxAllowedRowsToImport?: number;
}

const BulkAddRowsToStructuredTableFlow: React.ComponentType<BulkAddRowsToStructuredTableFlowProps> = (
  props
) => (
  <SpreadsheetBulkImportModalContainer
    importItemWord="table row"
    importType={IMPORT_TYPE.STRUCTURED_TABLE}
    initialSettings={{
      initialContainerSchemaId: props.containerSchemaId ?? null,
      initialTagSchemaId: props.tagSchemaId ?? null,
      initialSchemaInterfaceId: props.schemaInterfaceId ?? null,
      initialResultSchemaId: props.resultSchemaId ?? null,
      initialProcessTaskSchemaId: props.processTaskSchemaId ?? null,
      initialProcessOutputSchemaId: props.processOutputSchemaId ?? null,
      initialPlateSchemaId: props.plateSchemaId ?? null,
      initialBoxSchemaId: props.boxSchemaId ?? null,
    }}
    onImportFromSpreadsheet={() => {
      SpreadsheetBulkImportActions.getNewRowsForStructuredTableImport(
        (parsedRows: Array<ImportedStructuredTableRow>) => {
          props.onRequestSubmit(parsedRows);
        }
      );
    }}
    onRequestClose={SpreadsheetBulkImportActions.closeModal}
    panes={getPanes(props.spreadsheetColumnTypes, props.writableSchemaFieldIds, props.resultSchemaId)}
    title="Import into table"
    maxAllowedRowsToImport={props.maxAllowedRowsToImport}
  />
);

export default BulkAddRowsToStructuredTableFlow;
